// P2b-3 §6.1 + round-1 polish Fix 5: apply the filter-bar state to a
// row set. Pure helper so both Meta and Google grids share the
// substring + multi-select + status-stage logic.
//
// Search runs over Location name, Campaign name (override-aware), and
// AM display name. Substring case-insensitive. Empty search matches
// every row.

import type { Account } from '../types/account.js';
import type { AccountCampaign } from '../types/accountCampaign.js';
import type { Campaign } from '../types/campaign.js';
import type { CurrentUser } from '../types/user.js';
import { formatUserDisplayName } from './formatUserDisplayName.js';
import { resolveGroupTag } from './groupTag.js';
import {
  matchesAssetPresence,
  matchesBuildStatus,
  buildStatusForPlatform,
} from './assetBuildFilters.js';
import { NO_GROUP, UNASSIGNED_BUILDER, type GridFilters } from './gridFilterUrl.js';

export type ApplyArgs = {
  rows: AccountCampaign[];
  filters: GridFilters;
  accountsById: Map<string, Account>;
  campaignsById: Map<string, Campaign>;
  usersById: Map<string, CurrentUser>;
  // Round-1 polish Fix 5: platform drives the Status stage predicate
  // (Meta vs Google have different stage names). Builder predicate
  // also reads platform-specific fields.
  platform: 'meta' | 'google';
  // Round-1 polish Fix 6: current user uid for the "Assigned to me"
  // toggle. Always builder-only (per the planning answer Q4) — AMs
  // get a "my work" view in P3.
  currentUserUid: string;
};

export function applyGridFilters({
  rows,
  filters,
  accountsById,
  campaignsById,
  usersById,
  platform,
  currentUserUid,
}: ApplyArgs): AccountCampaign[] {
  const search = filters.search.trim().toLowerCase();
  const amSet = new Set(filters.am);
  const campaignSet = new Set(filters.campaignIds);
  const groupSet = new Set(filters.groups);
  const builderSet = new Set(filters.builders);
  const builderField: 'metaBuilderId' | 'googleBuilderId' =
    platform === 'meta' ? 'metaBuilderId' : 'googleBuilderId';
  // Build-status applies on both role grids, each restricted to its own
  // platform's options (Meta: main/local/organic; Google: googleNotBuilt).
  // Computed once — filters are constant across the row loop.
  const activeBuildStatus = buildStatusForPlatform(filters.buildStatus, platform);
  const out: AccountCampaign[] = [];
  for (const row of rows) {
    if (amSet.size > 0) {
      if (!row.assignedAmId || !amSet.has(row.assignedAmId)) continue;
    }
    if (campaignSet.size > 0) {
      if (!row.campaignId || !campaignSet.has(row.campaignId)) continue;
    }
    if (groupSet.size > 0) {
      const tag = resolveGroupTag(row.accountId, accountsById);
      const key = tag === null ? NO_GROUP : tag.id;
      if (!groupSet.has(key)) continue;
    }
    const builderId = row[builderField] ?? null;
    if (builderSet.size > 0) {
      // Selecting __unassigned__ matches null-builder rows; selecting
      // a uid matches that builder. The two coexist in one set.
      const matchesUnassigned =
        builderSet.has(UNASSIGNED_BUILDER) && builderId === null;
      const matchesUid = builderId !== null && builderSet.has(builderId);
      if (!matchesUnassigned && !matchesUid) continue;
    }
    if (filters.assignedToMe) {
      if (builderId !== currentUserUid) continue;
    }
    // Asset-presence is Meta-only — the Google grid never renders the control,
    // so it stays empty there; gate defensively against a hand-crafted Google
    // URL carrying it.
    if (platform === 'meta') {
      if (!matchesAssetPresence(row, filters.assetPresence)) continue;
    }
    // Build-status applies on both grids (Meta options on Meta, Google option
    // on Google) — activeBuildStatus is already platform-restricted.
    if (!matchesBuildStatus(row, activeBuildStatus)) continue;
    if (filters.status !== 'all') {
      if (!matchesStatus(row, filters.status, platform)) continue;
    }
    if (search.length > 0) {
      const acc = accountsById.get(row.accountId);
      const camp = row.campaignId ? campaignsById.get(row.campaignId) : null;
      const am = row.assignedAmId ? usersById.get(row.assignedAmId) : null;
      const haystack = [
        acc?.name ?? '',
        row.campaignNameOverride ?? camp?.name ?? '',
        am ? formatUserDisplayName(am) : '',
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) continue;
    }
    out.push(row);
  }
  return out;
}

// Status stage predicates. Independent — a row matches a stage iff its
// checkbox state satisfies the stage's individual predicate. Per the
// planning answer Q2: handles non-canonical flip orders gracefully —
// e.g. Live=true Built=false matches "Live, not post-check" even if it
// wouldn't match "Built, not live".
function matchesStatus(
  row: AccountCampaign,
  status: GridFilters['status'],
  platform: 'meta' | 'google',
): boolean {
  if (platform === 'meta') {
    switch (status) {
      case 'not-built':
        return row.metaBuilt === false;
      case 'built-not-live':
        return row.metaBuilt === true && row.metaTurnedOn === false;
      case 'live-not-postcheck':
        return row.metaTurnedOn === true && row.metaPostRolloutCheck === false;
      case 'complete':
        return row.metaBuilt && row.metaTurnedOn && row.metaPostRolloutCheck;
      default:
        // 'all' / unknown / cross-platform stage → no filtering
        return true;
    }
  }
  switch (status) {
    case 'not-built':
      return row.googleBuilt === false;
    case 'built-not-scheduled':
      return row.googleBuilt === true && row.googleScheduled === false;
    case 'scheduled-not-postcheck':
      return row.googleScheduled === true && row.googlePostRolloutCheck === false;
    case 'complete':
      return row.googleBuilt && row.googleScheduled && row.googlePostRolloutCheck;
    default:
      return true;
  }
}

// Derive the option lists for the filter bar from the rows currently
// in scope (i.e. the gate-passing rows, ignoring filter-bar state —
// so toggling the dropdown doesn't shrink its own option set).
export function deriveGridOptions(args: {
  rows: AccountCampaign[];
  accountsById: Map<string, Account>;
  campaignsById: Map<string, Campaign>;
  usersById: Map<string, CurrentUser>;
  platform: 'meta' | 'google';
}): {
  ams: CurrentUser[];
  campaigns: Campaign[];
  // Round-1 polish Fix 5: per-grid groups + builders. `hasNoGroup`
  // tells the UI whether to show the "(no group)" sentinel option.
  groups: Array<{ id: string; name: string }>;
  hasNoGroup: boolean;
  builders: CurrentUser[];
  hasUnassignedBuilder: boolean;
} {
  const amIds = new Set<string>();
  const campaignIds = new Set<string>();
  const builderIds = new Set<string>();
  const groupIds = new Map<string, string>(); // id → name
  let hasNoGroup = false;
  let hasUnassignedBuilder = false;
  const builderField = args.platform === 'meta' ? 'metaBuilderId' : 'googleBuilderId';
  for (const row of args.rows) {
    if (row.assignedAmId) amIds.add(row.assignedAmId);
    if (row.campaignId) campaignIds.add(row.campaignId);
    const builderId = row[builderField] ?? null;
    if (builderId) builderIds.add(builderId);
    else hasUnassignedBuilder = true;
    const tag = resolveGroupTag(row.accountId, args.accountsById);
    if (tag === null) hasNoGroup = true;
    else groupIds.set(tag.id, tag.name);
  }
  const ams: CurrentUser[] = [];
  for (const id of amIds) {
    const u = args.usersById.get(id);
    if (u) ams.push(u);
  }
  ams.sort((a, b) => formatUserDisplayName(a).localeCompare(formatUserDisplayName(b)));
  const campaigns: Campaign[] = [];
  for (const id of campaignIds) {
    const c = args.campaignsById.get(id);
    if (c) campaigns.push(c);
  }
  campaigns.sort((a, b) => a.name.localeCompare(b.name));
  const groups = [...groupIds.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const builders: CurrentUser[] = [];
  for (const id of builderIds) {
    const u = args.usersById.get(id);
    if (u) builders.push(u);
  }
  builders.sort((a, b) =>
    formatUserDisplayName(a).localeCompare(formatUserDisplayName(b)),
  );
  return { ams, campaigns, groups, hasNoGroup, builders, hasUnassignedBuilder };
}
